import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type WheelEvent as ReactWheelEvent } from "react";

import type { FilamentBackendTarget } from "./ThreadV5FilamentModel";
import { formatPanels } from "./ThreadV5Flow360Estimate";
import type { FilamentPreviewCaseId, ThreadFilamentGeometryArtifact } from "./ThreadV5FilamentArtifacts";

export interface DemoGeometry {
  bounds: { min: number[]; max: number[] };
  body: { points: number[][]; faces: number[][]; pressureCoefficient: number[] };
  wake: { points: number[][]; faces: number[][]; faceReveal: number[]; tipVortices?: number[][][] };
}

export interface DemoRun {
  summary: {
    backend: string;
    bodyCells: number;
    wakeCells: number;
    wakePoints: number;
    wakeIterations: number;
    pressureCoefficientMin: number;
    pressureCoefficientMax: number;
    filamentLength: number;
    motionPitchDeg: number;
    alphaDeg: number;
    betaDeg: number;
    tipVortexStrength: number;
    solveTime: number;
  };
  geometry: DemoGeometry;
}

export interface DemoCase {
  id: FilamentPreviewCaseId;
  label: string;
  runs: Record<string, DemoRun>;
}

export interface DemoData {
  cases: DemoCase[];
}

export type FilamentPrimitiveKind = "delta-wing" | "nacelle" | "straight-wing" | "swept-wing";
export type FilamentMotionMode = "fixed" | "pitch-oscillation";
export type FilamentWakeMode = "steady" | "unsteady";

export interface FilamentPrimitiveSpec {
  kind: FilamentPrimitiveKind;
  motionMode: FilamentMotionMode;
  span: number;
  chord: number;
  sweepDeg: number;
  dihedralDeg: number;
  camber: number;
  alphaDeg: number;
  betaDeg: number;
  wakeLength: number;
  wakeMode: FilamentWakeMode;
  oscillationAmplitudeDeg: number;
  oscillationFrequency: number;
}

export const DEFAULT_FILAMENT_PRIMITIVE_SPEC: FilamentPrimitiveSpec = {
  kind: "swept-wing",
  motionMode: "fixed",
  span: 2,
  chord: 1.05,
  sweepDeg: 18,
  dihedralDeg: 2,
  camber: 0.055,
  alphaDeg: 4,
  betaDeg: 0,
  wakeLength: 1.9,
  wakeMode: "steady",
  oscillationAmplitudeDeg: 8,
  oscillationFrequency: 1,
};

interface CameraState {
  yaw: number;
  pitch: number;
  zoom: number;
}

interface ProjectedPoint {
  x: number;
  y: number;
  depth: number;
}

interface DragState {
  x: number;
  y: number;
  yaw: number;
  pitch: number;
}

function formatNumber(value: number | null | undefined): string {
  if (value == null) return "n/a";
  return value.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

function lerp(start: number, end: number, amount: number): number {
  return start + (end - start) * amount;
}

function cpColor(cp: number, min: number, max: number): string {
  const span = Math.max(max - min, 1e-9);
  const t = Math.min(1, Math.max(0, (cp - min) / span));
  if (t < 0.5) {
    const local = t / 0.5;
    return `rgb(${lerp(36, 125, local)}, ${lerp(99, 211, local)}, ${lerp(235, 252, local)})`;
  }
  const local = (t - 0.5) / 0.5;
  return `rgb(${lerp(125, 249, local)}, ${lerp(211, 115, local)}, ${lerp(252, 22, local)})`;
}

function rotatePoint(point: number[], center: number[], scale: number, camera: CameraState): number[] {
  const x = ((point[0] ?? 0) - (center[0] ?? 0)) / scale;
  const y = ((point[1] ?? 0) - (center[1] ?? 0)) / scale;
  const z = ((point[2] ?? 0) - (center[2] ?? 0)) / scale;
  const yawCos = Math.cos(camera.yaw);
  const yawSin = Math.sin(camera.yaw);
  const pitchCos = Math.cos(camera.pitch);
  const pitchSin = Math.sin(camera.pitch);
  const yawX = x * yawCos - y * yawSin;
  const yawY = x * yawSin + y * yawCos;
  const pitchY = yawY * pitchCos - z * pitchSin;
  const pitchZ = yawY * pitchSin + z * pitchCos;
  return [yawX, pitchY, pitchZ];
}

function projectPoint(rotated: number[], width: number, height: number, zoom: number): ProjectedPoint {
  const focal = 3.2;
  const perspective = focal / Math.max(0.35, focal - (rotated[1] ?? 0));
  const viewScale = Math.min(width, height) * 0.82 * zoom * perspective;
  return {
    x: width * 0.5 + (rotated[0] ?? 0) * viewScale,
    y: height * 0.56 - (rotated[2] ?? 0) * viewScale,
    depth: rotated[1] ?? 0,
  };
}

function averageFaceValue(face: number[], values: number[]): number {
  if (!face.length) return 0;
  return face.reduce((sum, index) => sum + (values[index] ?? 0), 0) / face.length;
}

function drawFace(
  context: CanvasRenderingContext2D,
  projected: ProjectedPoint[],
  face: number[],
  fill: string,
  stroke: string,
  lineWidth: number,
) {
  const first = projected[face[0] ?? -1];
  if (face.length < 3 || !first) return;
  context.beginPath();
  context.moveTo(first.x, first.y);
  for (let index = 1; index < face.length; index += 1) {
    const point = projected[face[index] ?? -1];
    if (point) context.lineTo(point.x, point.y);
  }
  context.closePath();
  context.fillStyle = fill;
  context.fill();
  context.strokeStyle = stroke;
  context.lineWidth = lineWidth;
  context.stroke();
}

function drawTriad(context: CanvasRenderingContext2D, width: number, height: number, camera: CameraState) {
  const origin = [width - 70, height - 52];
  const axes = [
    { label: "x", vector: rotatePoint([1, 0, 0], [0, 0, 0], 1, camera), color: "#f97316" },
    { label: "y", vector: rotatePoint([0, 1, 0], [0, 0, 0], 1, camera), color: "#38bdf8" },
    { label: "z", vector: rotatePoint([0, 0, 1], [0, 0, 0], 1, camera), color: "#a7f3d0" },
  ];
  context.font = "700 11px system-ui, sans-serif";
  axes.forEach((axis) => {
    const end = [origin[0]! + (axis.vector[0] ?? 0) * 24, origin[1]! - (axis.vector[2] ?? 0) * 24];
    context.beginPath();
    context.moveTo(origin[0]!, origin[1]!);
    context.lineTo(end[0]!, end[1]!);
    context.strokeStyle = axis.color;
    context.lineWidth = 2;
    context.stroke();
    context.fillStyle = axis.color;
    context.fillText(axis.label, end[0]! + 4, end[1]! + 4);
  });
}

function clampNumber(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function finiteNumber(value: number | undefined, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function normalizedTime(value: number): number {
  return ((value % 1) + 1) % 1;
}

function degToRad(value: number): number {
  return (value * Math.PI) / 180;
}

function addVector(left: number[], right: number[]): number[] {
  return [(left[0] ?? 0) + (right[0] ?? 0), (left[1] ?? 0) + (right[1] ?? 0), (left[2] ?? 0) + (right[2] ?? 0)];
}

function subtractVector(left: number[], right: number[]): number[] {
  return [(left[0] ?? 0) - (right[0] ?? 0), (left[1] ?? 0) - (right[1] ?? 0), (left[2] ?? 0) - (right[2] ?? 0)];
}

function scaleVector(vector: number[], scale: number): number[] {
  return [(vector[0] ?? 0) * scale, (vector[1] ?? 0) * scale, (vector[2] ?? 0) * scale];
}

function dotVector(left: number[], right: number[]): number {
  return (left[0] ?? 0) * (right[0] ?? 0) + (left[1] ?? 0) * (right[1] ?? 0) + (left[2] ?? 0) * (right[2] ?? 0);
}

function crossVector(left: number[], right: number[]): number[] {
  return [
    (left[1] ?? 0) * (right[2] ?? 0) - (left[2] ?? 0) * (right[1] ?? 0),
    (left[2] ?? 0) * (right[0] ?? 0) - (left[0] ?? 0) * (right[2] ?? 0),
    (left[0] ?? 0) * (right[1] ?? 0) - (left[1] ?? 0) * (right[0] ?? 0),
  ];
}

function vectorLength(vector: number[]): number {
  return Math.sqrt(dotVector(vector, vector));
}

function clampVectorLength(vector: number[], maxLength: number): number[] {
  const length = vectorLength(vector);
  if (length <= maxLength || length < 1e-9) return vector;
  return scaleVector(vector, maxLength / length);
}

function oscillationValue(spec: FilamentPrimitiveSpec, simulationTime: number): number {
  if (spec.motionMode === "fixed") return 0;
  return Math.sin(2 * Math.PI * spec.oscillationFrequency * normalizedTime(simulationTime));
}

function oscillationPitchDeg(spec: FilamentPrimitiveSpec, simulationTime: number): number {
  return spec.motionMode === "pitch-oscillation" ? spec.oscillationAmplitudeDeg * oscillationValue(spec, simulationTime) : 0;
}

function effectiveAlphaDeg(spec: FilamentPrimitiveSpec, simulationTime: number): number {
  return spec.alphaDeg + oscillationPitchDeg(spec, simulationTime);
}

function flowDirection(spec: FilamentPrimitiveSpec, simulationTime: number): number[] {
  const alpha = degToRad(effectiveAlphaDeg(spec, simulationTime));
  const beta = degToRad(spec.betaDeg);
  return [Math.cos(alpha) * Math.cos(beta), Math.sin(beta), -Math.sin(alpha) * Math.cos(beta)];
}

function liftStrength(spec: FilamentPrimitiveSpec, simulationTime: number): number {
  return clampNumber(0.05 * effectiveAlphaDeg(spec, simulationTime) + 2.4 * spec.camber, -1.15, 1.15);
}

function boundCirculationStrength(spec: FilamentPrimitiveSpec, simulationTime: number): number {
  const unsteadyBoost = spec.wakeMode === "unsteady" ? 1 + 0.1 * oscillationValue(spec, simulationTime) : 1;
  return clampNumber(liftStrength(spec, simulationTime) * spec.chord * 0.34 * unsteadyBoost, -0.52, 0.52);
}

function spanCirculation(spec: FilamentPrimitiveSpec, normalizedY: number, simulationTime: number): number {
  const spanShape = Math.sqrt(Math.max(0, 1 - normalizedY * normalizedY));
  const taperCorrection = spec.kind === "delta-wing" ? 0.72 + 0.28 * (1 - Math.abs(normalizedY)) : 1;
  return boundCirculationStrength(spec, simulationTime) * spanShape * taperCorrection;
}

function trailingVorticityStrength(spec: FilamentPrimitiveSpec, normalizedY: number, simulationTime: number): number {
  const dy = 0.035;
  const upper = spanCirculation(spec, clampNumber(normalizedY + dy, -1, 1), simulationTime);
  const lower = spanCirculation(spec, clampNumber(normalizedY - dy, -1, 1), simulationTime);
  return clampNumber((upper - lower) / (2 * dy) * 0.16, -0.34, 0.34);
}

function inducedVelocityFromSegment(point: number[], start: number[], end: number[], circulation: number, coreRadius: number): number[] {
  const r1 = subtractVector(point, start);
  const r2 = subtractVector(point, end);
  const r0 = subtractVector(end, start);
  const r1Length = vectorLength(r1);
  const r2Length = vectorLength(r2);
  if (Math.abs(circulation) < 1e-9 || r1Length < coreRadius || r2Length < coreRadius) return [0, 0, 0];
  const cross = crossVector(r1, r2);
  const denominator = dotVector(cross, cross) + coreRadius ** 4;
  if (denominator < 1e-12) return [0, 0, 0];
  const kernel = dotVector(r0, subtractVector(scaleVector(r1, 1 / r1Length), scaleVector(r2, 1 / r2Length))) / denominator;
  return scaleVector(cross, (circulation / (4 * Math.PI)) * kernel);
}

function applyWingMotion(point: number[], spec: FilamentPrimitiveSpec, simulationTime: number): number[] {
  const pitchDeg = oscillationPitchDeg(spec, simulationTime);
  if (Math.abs(pitchDeg) < 1e-9) return point;
  const pitch = degToRad(pitchDeg);
  const pivotX = -0.29 * spec.chord;
  const x = point[0] ?? 0;
  const y = point[1] ?? 0;
  const z = point[2] ?? 0;
  const xRel = x - pivotX;
  const heave = 0.035 * spec.span * oscillationValue(spec, simulationTime);
  return [
    pivotX + xRel * Math.cos(pitch) + z * Math.sin(pitch),
    y,
    -xRel * Math.sin(pitch) + z * Math.cos(pitch) + heave,
  ];
}

function hashString(value: string): number {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function primitiveLabel(spec: FilamentPrimitiveSpec): string {
  if (spec.kind === "delta-wing") return "Delta wing primitive";
  if (spec.kind === "nacelle") return "Nacelle primitive";
  if (spec.kind === "straight-wing") return "Straight wing primitive";
  return "Swept wing primitive";
}

function parseLabelDegrees(label: string, tokens: string[], fallback: number): number {
  const pattern = new RegExp(`(?:${tokens.join("|")})\\s*[:=_-]?\\s*(-?\\d+(?:\\.\\d+)?)`, "i");
  const match = label.match(pattern);
  const parsed = match?.[1] ? Number(match[1]) : Number.NaN;
  return Number.isFinite(parsed) ? parsed : fallback;
}

function normalizePrimitiveSpec(spec: FilamentPrimitiveSpec): FilamentPrimitiveSpec {
  return {
    kind: spec.kind,
    motionMode: spec.motionMode,
    span: clampNumber(spec.span, 0.6, 8),
    chord: clampNumber(spec.chord, 0.25, 4),
    sweepDeg: clampNumber(spec.sweepDeg, -35, 65),
    dihedralDeg: clampNumber(spec.dihedralDeg, -20, 20),
    camber: clampNumber(spec.camber, -0.08, 0.16),
    alphaDeg: clampNumber(finiteNumber(spec.alphaDeg, DEFAULT_FILAMENT_PRIMITIVE_SPEC.alphaDeg), -15, 20),
    betaDeg: clampNumber(finiteNumber(spec.betaDeg, DEFAULT_FILAMENT_PRIMITIVE_SPEC.betaDeg), -20, 20),
    wakeLength: clampNumber(spec.wakeLength, 0.2, 8),
    wakeMode: spec.wakeMode,
    oscillationAmplitudeDeg: clampNumber(spec.oscillationAmplitudeDeg, 0, 25),
    oscillationFrequency: clampNumber(spec.oscillationFrequency, 0.1, 5),
  };
}

export function deriveFilamentPrimitiveSpec(source: ThreadFilamentGeometryArtifact | null): FilamentPrimitiveSpec {
  if (!source) return DEFAULT_FILAMENT_PRIMITIVE_SPEC;
  const label = `${source.name} ${source.kindLabel} ${source.sourceLabel}`.toLowerCase();
  const hash = hashString(`${source.id}:${source.name}`);
  const kind: FilamentPrimitiveKind = /(nacelle|duct|pod|axisymmetric)/i.test(label)
    ? "nacelle"
    : /(delta|gap|gai|fighter)/i.test(label)
      ? "delta-wing"
      : /(low|straight|tailplane|tail)/i.test(label)
        ? "straight-wing"
        : "swept-wing";
  return normalizePrimitiveSpec({
    kind,
    motionMode: /oscillat|pitch|dynamic|unsteady|transient/i.test(label) ? "pitch-oscillation" : "fixed",
    span: kind === "nacelle" ? 1 + (hash % 5) * 0.08 : 1.7 + (hash % 7) * 0.16,
    chord: kind === "nacelle" ? 1.7 + (hash % 4) * 0.12 : 0.82 + ((hash >> 3) % 6) * 0.08,
    sweepDeg: kind === "straight-wing" ? ((hash >> 4) % 7) - 3 : kind === "delta-wing" ? 38 + ((hash >> 5) % 12) : 12 + ((hash >> 6) % 18),
    dihedralDeg: /high/i.test(label) ? 8 : /low/i.test(label) ? -5 : ((hash >> 8) % 11) - 5,
    camber: kind === "nacelle" ? 0.02 : 0.035 + ((hash >> 9) % 7) * 0.008,
    alphaDeg: parseLabelDegrees(label, ["alpha", "aoa", "angleofattack", "angle_of_attack"], kind === "nacelle" ? 0 : 4),
    betaDeg: parseLabelDegrees(label, ["beta", "sideslip", "angleofsideslip", "angle_of_sideslip"], 0),
    wakeLength: 1.45 + ((hash >> 11) % 9) * 0.13,
    wakeMode: /unsteady|transient|dynamic/i.test(label) ? "unsteady" : "steady",
    oscillationAmplitudeDeg: 5 + ((hash >> 17) % 9),
    oscillationFrequency: 0.6 + ((hash >> 20) % 8) * 0.15,
  });
}

function wingSurfacePoint(spanIndex: number, chordIndex: number, sideSign: number, spanSegments: number, chordSegments: number, spec: FilamentPrimitiveSpec, simulationTime = 0): number[] {
  const normalized = normalizePrimitiveSpec(spec);
  const eta = spanIndex / spanSegments;
  const normalizedY = -1 + eta * 2;
  const absEta = Math.abs(normalizedY);
  const xsi = chordIndex / chordSegments;
  const taper = normalized.kind === "delta-wing" ? 1 - 0.62 * absEta : normalized.kind === "straight-wing" ? 1 : 1 - 0.28 * absEta;
  const chord = normalized.chord * Math.max(0.28, taper);
  const halfSpan = normalized.span * 0.5;
  const y = normalizedY * halfSpan;
  const sweepOffset = Math.tan(degToRad(normalized.sweepDeg)) * absEta * halfSpan * 0.42;
  const leadingEdge = -0.54 * normalized.chord + sweepOffset;
  const dihedral = Math.tan(degToRad(normalized.dihedralDeg)) * Math.abs(y) * 0.18;
  const camber = normalized.camber * Math.sin(Math.PI * xsi) * (1 - 0.2 * absEta);
  const thickness = 0.032 * Math.sin(Math.PI * xsi) * (1 - 0.45 * absEta);
  const twist = -0.035 * absEta * xsi;
  return applyWingMotion([leadingEdge + chord * xsi, y, camber + sideSign * thickness + twist + dihedral], normalized, simulationTime);
}

type WakeGrid = number[][][];

interface WakeSourceSegment {
  start: number[];
  end: number[];
  circulation: number;
}

function wakeShedTime(spec: FilamentPrimitiveSpec, simulationTime: number, wakeFraction: number): number {
  return spec.wakeMode === "unsteady" || spec.motionMode === "pitch-oscillation" ? simulationTime - wakeFraction * 0.62 : simulationTime;
}

function makeInitialWakeGrid(spec: FilamentPrimitiveSpec, spanSegments: number, chordSegments: number, wakeSegments: number, simulationTime: number): WakeGrid {
  const rows: WakeGrid = [];
  for (let wakeIndex = 0; wakeIndex <= wakeSegments; wakeIndex += 1) {
    const s = wakeIndex / wakeSegments;
    const shedTime = wakeShedTime(spec, simulationTime, s);
    const direction = flowDirection(spec, shedTime);
    const row: number[][] = [];
    for (let spanIndex = 0; spanIndex <= spanSegments; spanIndex += 1) {
      const trailingEdge = wingSurfacePoint(spanIndex, chordSegments, 0, spanSegments, chordSegments, spec, shedTime);
      row.push(addVector(trailingEdge, scaleVector(direction, spec.wakeLength * s)));
    }
    rows.push(row);
  }
  return rows;
}

function wakeGridPoint(rows: WakeGrid, wakeIndex: number, spanIndex: number): number[] {
  return rows[wakeIndex]?.[spanIndex] ?? [0, 0, 0];
}

function buildWakeSourceSegments(rows: WakeGrid, spec: FilamentPrimitiveSpec, spanSegments: number, chordSegments: number, wakeSegments: number, simulationTime: number): WakeSourceSegment[] {
  const segments: WakeSourceSegment[] = [];
  const spanStride = 6;
  const wakeStride = 7;
  const boundChordIndex = Math.max(1, Math.round(chordSegments * 0.25));
  for (let spanIndex = 0; spanIndex < spanSegments; spanIndex += spanStride) {
    const nextSpanIndex = Math.min(spanSegments, spanIndex + spanStride);
    const start = wingSurfacePoint(spanIndex, boundChordIndex, 0, spanSegments, chordSegments, spec, simulationTime);
    const end = wingSurfacePoint(nextSpanIndex, boundChordIndex, 0, spanSegments, chordSegments, spec, simulationTime);
    const eta = -1 + ((spanIndex + nextSpanIndex) / spanSegments);
    segments.push({ start, end, circulation: spanCirculation(spec, eta, simulationTime) });
  }

  const stationIndexes = new Set<number>([0, spanSegments]);
  for (let spanIndex = 10; spanIndex < spanSegments; spanIndex += 10) stationIndexes.add(spanIndex);
  Array.from(stationIndexes).sort((left, right) => left - right).forEach((spanIndex) => {
    const eta = -1 + (2 * spanIndex) / spanSegments;
    const edgeCirculation = spanIndex === 0
      ? -boundCirculationStrength(spec, simulationTime)
      : spanIndex === spanSegments
        ? boundCirculationStrength(spec, simulationTime)
        : trailingVorticityStrength(spec, eta, simulationTime);
    if (Math.abs(edgeCirculation) < 1e-6) return;
    for (let wakeIndex = 0; wakeIndex < wakeSegments; wakeIndex += wakeStride) {
      const nextWakeIndex = Math.min(wakeSegments, wakeIndex + wakeStride);
      segments.push({
        start: wakeGridPoint(rows, wakeIndex, spanIndex),
        end: wakeGridPoint(rows, nextWakeIndex, spanIndex),
        circulation: edgeCirculation,
      });
    }
  });

  if (spec.wakeMode === "unsteady" || spec.motionMode === "pitch-oscillation") {
    for (let wakeIndex = wakeStride; wakeIndex < wakeSegments; wakeIndex += wakeStride) {
      const s = wakeIndex / wakeSegments;
      const shedTime = wakeShedTime(spec, simulationTime, s);
      const previousShedTime = wakeShedTime(spec, simulationTime, Math.max(0, s - wakeStride / wakeSegments));
      const shedCirculation = (boundCirculationStrength(spec, shedTime) - boundCirculationStrength(spec, previousShedTime)) * 0.45;
      if (Math.abs(shedCirculation) < 1e-5) continue;
      for (let spanIndex = 0; spanIndex < spanSegments; spanIndex += spanStride) {
        const nextSpanIndex = Math.min(spanSegments, spanIndex + spanStride);
        const eta = -1 + ((spanIndex + nextSpanIndex) / spanSegments);
        const spanShape = Math.sqrt(Math.max(0, 1 - eta * eta));
        segments.push({
          start: wakeGridPoint(rows, wakeIndex, spanIndex),
          end: wakeGridPoint(rows, wakeIndex, nextSpanIndex),
          circulation: shedCirculation * spanShape,
        });
      }
    }
  }

  return segments;
}

function smoothWakeGrid(rows: WakeGrid, initialRows: WakeGrid, spanSegments: number, wakeSegments: number): WakeGrid {
  const smoothed = rows.map((row) => row.map((point) => [...point]));
  for (let wakeIndex = 1; wakeIndex <= wakeSegments; wakeIndex += 1) {
    for (let spanIndex = 0; spanIndex <= spanSegments; spanIndex += 1) {
      const point = wakeGridPoint(rows, wakeIndex, spanIndex);
      const upstream = wakeGridPoint(rows, Math.max(0, wakeIndex - 1), spanIndex);
      const downstream = wakeGridPoint(rows, Math.min(wakeSegments, wakeIndex + 1), spanIndex);
      let blended = addVector(scaleVector(point, 0.7), addVector(scaleVector(upstream, 0.15), scaleVector(downstream, 0.15)));
      if (spanIndex > 0 && spanIndex < spanSegments) {
        const left = wakeGridPoint(rows, wakeIndex, spanIndex - 1);
        const right = wakeGridPoint(rows, wakeIndex, spanIndex + 1);
        blended = addVector(scaleVector(blended, 0.9), addVector(scaleVector(left, 0.05), scaleVector(right, 0.05)));
      }
      smoothed[wakeIndex]![spanIndex] = blended;
    }
  }
  smoothed[0] = initialRows[0]!.map((point) => [...point]);
  return smoothed;
}

function relaxFreeWakeGrid(initialRows: WakeGrid, spec: FilamentPrimitiveSpec, spanSegments: number, chordSegments: number, wakeSegments: number, simulationTime: number, solveIterations: number): WakeGrid {
  const passes = Math.max(1, Math.min(80, Math.round(solveIterations)));
  const coreRadius = Math.max(0.02, Math.min(spec.span, spec.chord) * 0.035);
  const timeStep = (0.26 * spec.wakeLength) / passes;
  const maxStep = Math.max(0.015, spec.span * 0.045);
  let rows = initialRows.map((row) => row.map((point) => [...point]));

  for (let pass = 0; pass < passes; pass += 1) {
    const sourceSegments = buildWakeSourceSegments(rows, spec, spanSegments, chordSegments, wakeSegments, simulationTime);
    const nextRows = rows.map((row) => row.map((point) => [...point]));
    for (let wakeIndex = 1; wakeIndex <= wakeSegments; wakeIndex += 1) {
      const s = wakeIndex / wakeSegments;
      for (let spanIndex = 0; spanIndex <= spanSegments; spanIndex += 1) {
        const point = wakeGridPoint(rows, wakeIndex, spanIndex);
        let inducedVelocity = [0, 0, 0];
        sourceSegments.forEach((segment) => {
          inducedVelocity = addVector(inducedVelocity, inducedVelocityFromSegment(point, segment.start, segment.end, segment.circulation, coreRadius));
        });
        const displacement = clampVectorLength(scaleVector(inducedVelocity, timeStep * (0.2 + 0.8 * s)), maxStep * (0.25 + 0.75 * s));
        nextRows[wakeIndex]![spanIndex] = addVector(point, displacement);
      }
    }
    nextRows[0] = initialRows[0]!.map((point) => [...point]);
    rows = smoothWakeGrid(nextRows, initialRows, spanSegments, wakeSegments);
  }
  return rows;
}

function flattenWakeGrid(rows: WakeGrid): number[][] {
  return rows.flatMap((row) => row.map((point) => [...point]));
}

function makeWingRun(backend: FilamentBackendTarget, spec: FilamentPrimitiveSpec, simulationTime = 0, solveIterations = 15): DemoRun {
  const normalized = normalizePrimitiveSpec(spec);
  const spanSegments = 60;
  const chordSegments = 20;
  const wakeSegments = 70;
  const wakeSolveIterations = Math.max(1, Math.min(80, Math.round(solveIterations)));
  const points: number[][] = [];
  const pressureCoefficient: number[] = [];
  const faces: number[][] = [];
  const pointIndex = (side: number, spanIndex: number, chordIndex: number) => side * (spanSegments + 1) * (chordSegments + 1) + spanIndex * (chordSegments + 1) + chordIndex;

  for (let side = 0; side < 2; side += 1) {
    const sideSign = side === 0 ? 1 : -1;
    for (let spanIndex = 0; spanIndex <= spanSegments; spanIndex += 1) {
      for (let chordIndex = 0; chordIndex <= chordSegments; chordIndex += 1) {
        const xsi = chordIndex / chordSegments;
        const point = wingSurfacePoint(spanIndex, chordIndex, sideSign, spanSegments, chordSegments, normalized, simulationTime);
        const y = point[1] ?? 0;
        const normalizedY = y / (normalized.span * 0.5);
        const absEta = Math.abs(normalizedY);
        const lift = liftStrength(normalized, simulationTime);
        const betaSkew = 0.014 * normalized.betaDeg * normalizedY;
        points.push(point);
        pressureCoefficient.push(side === 0
          ? -0.62 - 0.54 * lift + 1.28 * xsi + 0.14 * absEta + betaSkew
          : 0.14 + 0.34 * lift + 0.24 * xsi - 0.06 * absEta + betaSkew * 0.45);
      }
    }
  }

  for (let side = 0; side < 2; side += 1) {
    for (let spanIndex = 0; spanIndex < spanSegments; spanIndex += 1) {
      for (let chordIndex = 0; chordIndex < chordSegments; chordIndex += 1) {
        const face = [
          pointIndex(side, spanIndex, chordIndex),
          pointIndex(side, spanIndex + 1, chordIndex),
          pointIndex(side, spanIndex + 1, chordIndex + 1),
          pointIndex(side, spanIndex, chordIndex + 1),
        ];
        faces.push(side === 0 ? face : [...face].reverse());
      }
    }
  }

  const wakePoints: number[][] = [];
  const wakeFaces: number[][] = [];
  const faceReveal: number[] = [];
  const wakePointIndex = (wakeIndex: number, spanIndex: number) => wakeIndex * (spanSegments + 1) + spanIndex;
  const initialWakeRows = makeInitialWakeGrid(normalized, spanSegments, chordSegments, wakeSegments, simulationTime);
  const freeWakeRows = relaxFreeWakeGrid(initialWakeRows, normalized, spanSegments, chordSegments, wakeSegments, simulationTime, wakeSolveIterations);
  wakePoints.push(...flattenWakeGrid(freeWakeRows));
  for (let wakeIndex = 0; wakeIndex < wakeSegments; wakeIndex += 1) {
    for (let spanIndex = 0; spanIndex < spanSegments; spanIndex += 1) {
      wakeFaces.push([
        wakePointIndex(wakeIndex, spanIndex),
        wakePointIndex(wakeIndex + 1, spanIndex),
        wakePointIndex(wakeIndex + 1, spanIndex + 1),
        wakePointIndex(wakeIndex, spanIndex + 1),
      ]);
      faceReveal.push((wakeIndex + 1) / wakeSegments);
    }
  }
  const tipVortices: number[][][] = [0, spanSegments].map((tipSpanIndex) => freeWakeRows.map((row) => [...(row[tipSpanIndex] ?? [0, 0, 0])]));
  const maxAlpha = Math.max(Math.abs(normalized.alphaDeg), Math.abs(normalized.alphaDeg + normalized.oscillationAmplitudeDeg));
  const wakeLateral = Math.abs(Math.sin(degToRad(normalized.betaDeg))) * normalized.wakeLength + normalized.span * 0.18;
  const wakeVertical = Math.abs(Math.sin(degToRad(maxAlpha))) * normalized.wakeLength + normalized.span * 0.16;
  const pressureCoefficientMin = Math.min(...pressureCoefficient);
  const pressureCoefficientMax = Math.max(...pressureCoefficient);

  return {
    summary: {
      backend,
      bodyCells: faces.length,
      wakeCells: wakeFaces.length,
      wakePoints: wakePoints.length,
      wakeIterations: wakeSolveIterations,
      pressureCoefficientMin: backend === "cuda" ? pressureCoefficientMin + 0.003 : pressureCoefficientMin,
      pressureCoefficientMax,
      filamentLength: normalized.wakeLength,
      motionPitchDeg: oscillationPitchDeg(normalized, simulationTime),
      alphaDeg: effectiveAlphaDeg(normalized, simulationTime),
      betaDeg: normalized.betaDeg,
      tipVortexStrength: Math.abs(liftStrength(normalized, simulationTime)),
      solveTime: normalizedTime(simulationTime),
    },
    geometry: {
      bounds: {
        min: [-0.7 * normalized.chord, -normalized.span * 0.62 - wakeLateral, -0.3 - wakeVertical],
        max: [normalized.wakeLength + normalized.chord, normalized.span * 0.62 + wakeLateral, 0.42 + Math.abs(normalized.dihedralDeg) * 0.015 + wakeVertical],
      },
      body: { points, faces, pressureCoefficient },
      wake: { points: wakePoints, faces: wakeFaces, faceReveal, tipVortices },
    },
  };
}

function makeNacelleRun(backend: FilamentBackendTarget, spec: FilamentPrimitiveSpec = DEFAULT_FILAMENT_PRIMITIVE_SPEC, simulationTime = 0): DemoRun {
  const normalized = normalizePrimitiveSpec(spec);
  const axialSegments = 24;
  const radialSegments = 20;
  const wakeSegments = 66;
  const points: number[][] = [];
  const pressureCoefficient: number[] = [];
  const faces: number[][] = [];
  const pointIndex = (axialIndex: number, radialIndex: number) => axialIndex * radialSegments + (radialIndex % radialSegments);
  for (let axialIndex = 0; axialIndex <= axialSegments; axialIndex += 1) {
    const u = axialIndex / axialSegments;
    const x = -0.5 * normalized.chord + normalized.chord * u;
    const radius = normalized.span * (0.22 + 0.075 * Math.sin(Math.PI * u) - 0.03 * u);
    for (let radialIndex = 0; radialIndex < radialSegments; radialIndex += 1) {
      const theta = (radialIndex / radialSegments) * Math.PI * 2;
      points.push([x, radius * Math.cos(theta), radius * Math.sin(theta)]);
      pressureCoefficient.push(-0.62 * Math.sin(Math.PI * u) + 0.35 * u + 0.08 * Math.cos(theta));
    }
  }
  for (let axialIndex = 0; axialIndex < axialSegments; axialIndex += 1) {
    for (let radialIndex = 0; radialIndex < radialSegments; radialIndex += 1) {
      faces.push([
        pointIndex(axialIndex, radialIndex),
        pointIndex(axialIndex + 1, radialIndex),
        pointIndex(axialIndex + 1, radialIndex + 1),
        pointIndex(axialIndex, radialIndex + 1),
      ]);
    }
  }
  const wakePoints: number[][] = [];
  const wakeFaces: number[][] = [];
  const faceReveal: number[] = [];
  const wakePointIndex = (wakeIndex: number, radialIndex: number) => wakeIndex * radialSegments + (radialIndex % radialSegments);
  for (let wakeIndex = 0; wakeIndex <= wakeSegments; wakeIndex += 1) {
    const s = wakeIndex / wakeSegments;
    for (let radialIndex = 0; radialIndex < radialSegments; radialIndex += 1) {
      const shedTime = normalized.wakeMode === "unsteady" || normalized.motionMode === "pitch-oscillation" ? simulationTime - s * 0.62 : simulationTime;
      const direction = flowDirection(normalized, shedTime);
      const downstream = normalized.wakeLength * s;
      const theta = (radialIndex / radialSegments) * Math.PI * 2 + (0.035 + Math.abs(boundCirculationStrength(normalized, shedTime))) * 6 * s + 0.35 * s * oscillationValue(normalized, shedTime);
      const radius = normalized.span * (0.18 + 0.06 * s + 0.018 * s * oscillationValue(normalized, shedTime));
      const pulse = normalized.wakeMode === "unsteady" ? 0.05 * Math.sin(8 * Math.PI * (s + simulationTime) + theta) : 0;
      wakePoints.push([
        0.5 * normalized.chord + downstream * (direction[0] ?? 1),
        downstream * (direction[1] ?? 0) + radius * Math.cos(theta),
        downstream * (direction[2] ?? 0) + radius * Math.sin(theta) + 0.08 * s + pulse,
      ]);
    }
  }
  for (let wakeIndex = 0; wakeIndex < wakeSegments; wakeIndex += 1) {
    for (let radialIndex = 0; radialIndex < radialSegments; radialIndex += 1) {
      wakeFaces.push([
        wakePointIndex(wakeIndex, radialIndex),
        wakePointIndex(wakeIndex + 1, radialIndex),
        wakePointIndex(wakeIndex + 1, radialIndex + 1),
        wakePointIndex(wakeIndex, radialIndex + 1),
      ]);
      faceReveal.push((wakeIndex + 1) / wakeSegments);
    }
  }
  return {
    summary: {
      backend,
      bodyCells: faces.length,
      wakeCells: wakeFaces.length,
      wakePoints: wakePoints.length,
      wakeIterations: 15,
      pressureCoefficientMin: -1.1,
      pressureCoefficientMax: 0.72,
      filamentLength: normalized.wakeLength,
      motionPitchDeg: 0,
      alphaDeg: normalized.alphaDeg,
      betaDeg: normalized.betaDeg,
      tipVortexStrength: 0,
      solveTime: normalizedTime(simulationTime),
    },
    geometry: {
      bounds: {
        min: [-0.5 * normalized.chord, -normalized.span * 0.34 - Math.abs(Math.sin(degToRad(normalized.betaDeg))) * normalized.wakeLength, -normalized.span * 0.34 - Math.abs(Math.sin(degToRad(normalized.alphaDeg))) * normalized.wakeLength],
        max: [0.5 * normalized.chord + normalized.wakeLength, normalized.span * 0.34 + Math.abs(Math.sin(degToRad(normalized.betaDeg))) * normalized.wakeLength, normalized.span * 0.42 + Math.abs(Math.sin(degToRad(normalized.alphaDeg))) * normalized.wakeLength],
      },
      body: { points, faces, pressureCoefficient },
      wake: { points: wakePoints, faces: wakeFaces, faceReveal },
    },
  };
}

export function createFilamentPreviewRun(backend: FilamentBackendTarget, spec: FilamentPrimitiveSpec, simulationTime = 0, solveIterations = 15): DemoRun {
  const normalized = normalizePrimitiveSpec(spec);
  return normalized.kind === "nacelle" ? makeNacelleRun(backend, normalized, simulationTime) : makeWingRun(backend, normalized, simulationTime, solveIterations);
}

export function createFilamentPreviewDemoData(): DemoData {
  return {
    cases: [
      {
        id: "sweptwing",
        label: "Swept wing",
        runs: {
          webgpu: makeWingRun("webgpu", DEFAULT_FILAMENT_PRIMITIVE_SPEC),
          cuda: makeWingRun("cuda", DEFAULT_FILAMENT_PRIMITIVE_SPEC),
        },
      },
      {
        id: "nacelle",
        label: "Nacelle",
        runs: {
          webgpu: makeNacelleRun("webgpu", { ...DEFAULT_FILAMENT_PRIMITIVE_SPEC, kind: "nacelle", span: 1.1, chord: 1.8, alphaDeg: 0 }),
          cuda: makeNacelleRun("cuda", { ...DEFAULT_FILAMENT_PRIMITIVE_SPEC, kind: "nacelle", span: 1.1, chord: 1.8, alphaDeg: 0 }),
        },
      },
    ],
  };
}

export function ThreadV5FilamentGeometryPreview({
  source,
  backendTarget,
  primitiveSpec,
  wakeIterations,
}: {
  source: ThreadFilamentGeometryArtifact | null;
  backendTarget: FilamentBackendTarget;
  primitiveSpec?: FilamentPrimitiveSpec | null;
  wakeIterations: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const [wakeReveal, setWakeReveal] = useState(15);
  const [simulationTime, setSimulationTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [resolveCount, setResolveCount] = useState(0);
  const [camera, setCamera] = useState<CameraState>({
    yaw: backendTarget === "cuda" ? -0.72 : -0.58,
    pitch: 0.54,
    zoom: 1,
  });

  const resolvedPrimitiveSpec = useMemo(() => normalizePrimitiveSpec(primitiveSpec ?? deriveFilamentPrimitiveSpec(source)), [primitiveSpec, source]);
  const selectedRun = useMemo(() => createFilamentPreviewRun(backendTarget, resolvedPrimitiveSpec, simulationTime, wakeIterations), [backendTarget, resolvedPrimitiveSpec, simulationTime, wakeIterations]);
  const summary = selectedRun.summary;
  const canAnimate = resolvedPrimitiveSpec.motionMode === "pitch-oscillation" || resolvedPrimitiveSpec.wakeMode === "unsteady";

  useEffect(() => {
    setWakeReveal(summary.wakeIterations);
    setCamera((previous) => ({
      ...previous,
      yaw: backendTarget === "cuda" ? -0.72 : -0.58,
    }));
  }, [backendTarget, summary.wakeIterations]);

  useEffect(() => {
    if (!canAnimate) setIsPlaying(false);
  }, [canAnimate]);

  useEffect(() => {
    if (!isPlaying || !canAnimate || window.navigator.userAgent.includes("jsdom")) return undefined;
    let frameId = 0;
    let lastTimestamp = performance.now();
    const tick = (timestamp: number) => {
      const deltaSeconds = Math.min(0.08, Math.max(0, (timestamp - lastTimestamp) / 1000));
      lastTimestamp = timestamp;
      setSimulationTime((previous) => normalizedTime(previous + deltaSeconds * 0.28));
      frameId = requestAnimationFrame(tick);
    };
    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [canAnimate, isPlaying]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const geometry = selectedRun.geometry;
    const summary = selectedRun.summary;
    if (!canvas || !geometry || !summary) return undefined;
    if (window.navigator.userAgent.includes("jsdom")) return undefined;

    const draw = () => {
      const pixelRatio = window.devicePixelRatio || 1;
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      canvas.width = Math.max(1, Math.floor(width * pixelRatio));
      canvas.height = Math.max(1, Math.floor(height * pixelRatio));
      const context = canvas.getContext("2d");
      if (!context) return;
      context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      context.clearRect(0, 0, width, height);

      const boundsMin = geometry.bounds.min;
      const boundsMax = geometry.bounds.max;
      const center = boundsMin.map((value, index) => (value + (boundsMax[index] ?? value)) * 0.5);
      const span = Math.max(
        (boundsMax[0] ?? 0) - (boundsMin[0] ?? 0),
        (boundsMax[1] ?? 0) - (boundsMin[1] ?? 0),
        (boundsMax[2] ?? 0) - (boundsMin[2] ?? 0),
        1e-9,
      );
      const bodyProjected = geometry.body.points.map((point) => projectPoint(rotatePoint(point, center, span, camera), width, height, camera.zoom));
      const wakeProjected = geometry.wake.points.map((point) => projectPoint(rotatePoint(point, center, span, camera), width, height, camera.zoom));
      const revealFraction = wakeReveal / Math.max(1, summary.wakeIterations);
      const wakeDepth = wakeProjected.map((point) => point.depth);
      geometry.wake.faces
        .map((face, index) => ({
          face,
          reveal: geometry.wake.faceReveal[index] ?? 1,
          depth: averageFaceValue(face, wakeDepth),
        }))
        .filter((item) => item.reveal <= revealFraction + 1e-6)
        .sort((left, right) => left.depth - right.depth)
        .forEach((item) => drawFace(context, wakeProjected, item.face, "rgba(56, 189, 248, 0.15)", "rgba(125, 211, 252, 0.42)", 0.55));

      const pressure = geometry.body.pressureCoefficient;
      const bodyDepth = bodyProjected.map((point) => point.depth);
      geometry.body.faces
        .map((face) => ({
          face,
          depth: averageFaceValue(face, bodyDepth),
          cp: averageFaceValue(face, pressure),
        }))
        .sort((left, right) => left.depth - right.depth)
        .forEach((item) => drawFace(
          context,
          bodyProjected,
          item.face,
          cpColor(item.cp, summary.pressureCoefficientMin, summary.pressureCoefficientMax),
          "rgba(255, 255, 255, 0.18)",
          0.42,
        ));

      (geometry.wake.tipVortices ?? []).forEach((filament, filamentIndex) => {
        const projected = filament.map((point) => projectPoint(rotatePoint(point, center, span, camera), width, height, camera.zoom));
        const first = projected[0];
        if (!first) return;
        context.save();
        context.lineCap = "round";
        context.lineJoin = "round";
        context.shadowColor = filamentIndex === 0 ? "rgba(96, 165, 250, 0.75)" : "rgba(45, 212, 191, 0.75)";
        context.shadowBlur = 8;
        context.beginPath();
        context.moveTo(first.x, first.y);
        for (let index = 1; index < projected.length; index += 1) {
          const point = projected[index];
          if (point) context.lineTo(point.x, point.y);
        }
        context.strokeStyle = filamentIndex === 0 ? "rgba(147, 197, 253, 0.96)" : "rgba(94, 234, 212, 0.96)";
        context.lineWidth = 2.2;
        context.stroke();
        context.restore();
      });

      context.fillStyle = "rgba(226, 232, 240, 0.9)";
      context.font = "700 12px system-ui, sans-serif";
      context.fillText("drag to rotate · scroll to zoom · panel mesh + shed wake + tip vortices", 18, 24);
      drawTriad(context, width, height, camera);
    };

    draw();
    const observer = new ResizeObserver(draw);
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [camera, selectedRun, wakeReveal]);

  const onPointerDown = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    dragRef.current = {
      x: event.clientX,
      y: event.clientY,
      yaw: camera.yaw,
      pitch: camera.pitch,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!dragRef.current) return;
    const dx = event.clientX - dragRef.current.x;
    const dy = event.clientY - dragRef.current.y;
    setCamera((previous) => ({
      ...previous,
      yaw: dragRef.current ? dragRef.current.yaw + dx * 0.01 : previous.yaw,
      pitch: dragRef.current ? Math.max(-1.25, Math.min(1.25, dragRef.current.pitch + dy * 0.01)) : previous.pitch,
    }));
  };

  const onPointerUp = () => {
    dragRef.current = null;
  };

  const onWheel = (event: ReactWheelEvent<HTMLCanvasElement>) => {
    event.preventDefault();
    setCamera((previous) => ({
      ...previous,
      zoom: Math.max(0.55, Math.min(2.2, previous.zoom * (event.deltaY > 0 ? 0.92 : 1.08))),
    }));
  };

  const scaledWakeReveal = Math.min(summary?.wakeIterations ?? wakeReveal, Math.max(1, Math.round((wakeIterations / Math.max(1, wakeIterations)) * (summary?.wakeIterations ?? wakeReveal))));

  useEffect(() => {
    if (summary) setWakeReveal(Math.max(1, Math.min(summary.wakeIterations, scaledWakeReveal)));
  }, [scaledWakeReveal, summary.wakeIterations]);

  const resolveNextFrame = () => {
    setIsPlaying(false);
    setSimulationTime((previous) => normalizedTime(previous + 0.08));
    setResolveCount((previous) => previous + 1);
  };

  return (
    <section className="v5-filament-geometry-preview" aria-label="Filament panel and wake geometry">
      <div className="v5-filament-preview-header">
        <div>
          <span className="v5-eyebrow">Paneling + wake</span>
          <h2>{source?.name ?? primitiveLabel(resolvedPrimitiveSpec)}</h2>
          <p>{source ? `${source.kindLabel} · ${source.sourceLabel}` : `${primitiveLabel(resolvedPrimitiveSpec)} · ${resolvedPrimitiveSpec.wakeMode} wake · ${resolvedPrimitiveSpec.motionMode === "pitch-oscillation" ? "oscillating wing" : "fixed wing"}`}</p>
        </div>
        <span className="v5-chip">{backendTarget === "cuda" ? "CUDA view" : "WebGPU view"}</span>
      </div>
      <div className="v5-filament-canvas-shell">
        {selectedRun ? (
          <canvas
            aria-label="Interactive 3D panel mesh, shed wake, and tip vortices"
            className="v5-filament-geometry-canvas"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onWheel={onWheel}
            ref={canvasRef}
            role="img"
          />
        ) : (
          <div className="v5-import-empty">
            <strong>Loading panel geometry</strong>
            <p>The local Filament panel/wake preview is being prepared for this Thread geometry.</p>
          </div>
        )}
        <div className="v5-filament-preview-legend">
          <span>low Cp / suction</span>
          <i />
          <span>high Cp</span>
        </div>
      </div>
      <div className="v5-filament-preview-controls">
        <div className="v5-filament-time-controls">
          <button className="v5-primary-button" type="button" onClick={() => setIsPlaying((playing) => !playing)} disabled={!canAnimate}>
            {isPlaying ? "Pause wake" : "Play wake"}
          </button>
          <button className="v5-text-button" type="button" onClick={resolveNextFrame}>
            Resolve next frame
          </button>
          <span>frame {resolveCount} · t={summary.solveTime.toFixed(2)}</span>
        </div>
        <label>
          <span>Unsteady time / wing phase</span>
          <input
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={summary.solveTime}
            onChange={(event) => {
              setIsPlaying(false);
              setSimulationTime(Number(event.target.value));
            }}
          />
        </label>
        <label>
          <span>Wake/filament panels revealed</span>
          <input
            type="range"
            min={1}
            max={summary?.wakeIterations ?? 1}
            value={wakeReveal}
            onChange={(event) => setWakeReveal(Number(event.target.value))}
          />
        </label>
        <dl>
          <div><dt>Body panels</dt><dd>{formatPanels(summary?.bodyCells ?? source?.panelCount)}</dd></div>
          <div><dt>Wake panels</dt><dd>{formatPanels(summary?.wakeCells)}</dd></div>
          <div><dt>Wake points</dt><dd>{formatPanels(summary?.wakePoints)}</dd></div>
          <div><dt>Filament length</dt><dd>{formatNumber(summary?.filamentLength)} c</dd></div>
          <div><dt>Wing pitch</dt><dd>{formatNumber(summary?.motionPitchDeg)}°</dd></div>
          <div><dt>Alpha</dt><dd>{formatNumber(summary?.alphaDeg)}°</dd></div>
          <div><dt>Beta</dt><dd>{formatNumber(summary?.betaDeg)}°</dd></div>
          <div><dt>Tip vortex Γ*</dt><dd>{formatNumber(summary?.tipVortexStrength)}</dd></div>
          <div><dt>Cp range</dt><dd>{formatNumber(summary?.pressureCoefficientMin)} → {formatNumber(summary?.pressureCoefficientMax)}</dd></div>
        </dl>
      </div>
      <p className="v5-filament-preview-note">
        Primitive edits regenerate the preview solve immediately. The wake is seeded from trailing-edge points convecting with freestream, then relaxed by bound- and wake-filament induced velocity; use filament length, wake mode, solve iterations, time, and Resolve next frame to inspect the free-wake update before persisted mesh coordinates and solved wake geometry are written back to Thread.
      </p>
    </section>
  );
}
